import React from 'react';

import { Button } from '@trqk/components';
import { locale } from '@trqk/utils';

interface ConfirmGapAnalysisDeletionInput {
    gapAnalysisId: string;
    description: string;
    deleteGapAnalysis: DeleteGapAnalysis;
}

/**
 * A RenderContent component for a Toast which shows a confirmation dialog to make sure the user
 * really wants to delete a gap analysis report.
 * @param props {ToastRenderContentProps<ConfirmGapAnalysisDeletionInput>}
 */
export const ConfirmGapAnalysisDeletion = ({
    remove,
    input,
}: ToastRenderContentProps<ConfirmGapAnalysisDeletionInput>): JSX.Element => {
    const gapAnalysisId = input?.gapAnalysisId ?? null;
    const description = input?.description ?? null;
    const deleteGapAnalysis = input?.deleteGapAnalysis ?? null;

    const warning = description ? (
        <>
            <p>{locale.enUS.TOASTS.CONFIRM_GAP_ANALYSIS_DELETION.PART_1}</p>
            <p className="font-semibold pl-6 pt-2">{description}</p>
        </>
    ) : (
        <p className="font-semibold">
            {locale.enUS.TOASTS.CONFIRM_GAP_ANALYSIS_DELETION.PART_1_GENERIC}
        </p>
    );

    return (
        <>
            <div style={{ maxWidth: 350 }}>{warning}</div>
            <div className="mt-6">
                {
                    locale.enUS.MANAGE_STATEMENTS.TOASTS
                        .CONFIRM_STATEMENT_DELETION.PART_2
                }
            </div>
            <div className="mt-8 flex justify-end">
                <Button
                    color="danger-tertiary-light-bg"
                    autoWidth={true}
                    addClasses="mr-4"
                    handleClick={(): void => {
                        remove({
                            canceled: true,
                            success: false,
                        });
                    }}
                >
                    <div className="text-center mx-3">
                        {
                            locale.enUS.TOASTS.CONFIRM_GAP_ANALYSIS_DELETION
                                .BUTTONS.CANCEL
                        }
                    </div>
                </Button>
                <Button
                    color="danger-primary-light-bg"
                    autoWidth={true}
                    handleClick={(): void => {
                        if (deleteGapAnalysis !== null && gapAnalysisId) {
                            deleteGapAnalysis(gapAnalysisId)
                                .then(() => {
                                    remove({
                                        canceled: false,
                                        success: true,
                                    });
                                })
                                .catch(() => {
                                    remove({
                                        canceled: false,
                                        success: false,
                                    });
                                });
                        } else {
                            remove({
                                canceled: false,
                                success: false,
                            });
                        }
                    }}
                >
                    <div className="flex justify-center mx-3 w-32">
                        {
                            locale.enUS.TOASTS.CONFIRM_GAP_ANALYSIS_DELETION
                                .BUTTONS.DELETE
                        }
                    </div>
                </Button>
            </div>
        </>
    );
};
